import React, { useEffect, useMemo } from 'react';

import { appStoreUrl, googlePlayUrl } from '@/config/urls';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import {
	PROVIDER_CACHE_KEYS,
	readProviderCache,
	writeProviderCache,
} from '@/helpers/providerCache';
import { useEnvironment } from '@/providers/EnvironmentProvider';
import { isPlatform } from '@ionic/react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { PublicLayout } from '../PublicLayout';
import { AsaphButton } from '../shared/Button';
import { Loader } from '../shared/Loader';
import { Logo } from '../shared/Logo';
import styles from './VersionChecker.module.scss';

const isIOS = isPlatform('ios');

export const VERSION_CHECKER_QUERY_KEY = ['version'] as const;

type HealthCheckData = {
	apiVersion: string;
	appVersion: string;
	message: string;
};

const isHealthCheckData = (value: unknown): value is HealthCheckData => {
	if (!value || typeof value !== 'object') {
		return false;
	}

	return typeof (value as HealthCheckData).apiVersion === 'string';
};

const normalizeHealthCheckData = (
	value: unknown,
): HealthCheckData | null => {
	if (!value || typeof value !== 'object') {
		return null;
	}

	if (isHealthCheckData(value)) {
		return value;
	}

	const nestedData = (value as { data?: unknown }).data;
	return isHealthCheckData(nestedData) ? nestedData : null;
};

export const VersionChecker: React.FC = ({ children }) => {
	const {
		api: { url, version },
	} = useEnvironment();
	const { handleError } = useErrorHandler();
	const cachedVersion = useMemo(
		() =>
			normalizeHealthCheckData(
				readProviderCache<unknown>(PROVIDER_CACHE_KEYS.version),
			),
		[],
	);
	const hasCachedVersion = !!cachedVersion?.apiVersion;
	const {
		data: fetchedHealth,
		isLoading: isHealthLoading,
		isFetching: isHealthFetching,
		error,
	} = useQuery({
		queryKey: [...VERSION_CHECKER_QUERY_KEY, url],
		queryFn: async () => {
			const response = await axios.get<HealthCheckData>(`${url}/health`);
			return response.data;
		},
		enabled: !!url,
		retry: 3,
		networkMode: 'always',
		staleTime: 0,
		refetchOnMount: 'always',
	});

	const healthRes = fetchedHealth ?? cachedVersion;

	useEffect(() => {
		if (fetchedHealth?.apiVersion) {
			writeProviderCache(PROVIDER_CACHE_KEYS.version, fetchedHealth);
		}
	}, [fetchedHealth]);

	const isWaitingForInitialFetch =
		!hasCachedVersion && (isHealthLoading || isHealthFetching);

	if (!healthRes?.apiVersion) {
		if (error && !isWaitingForInitialFetch) {
			handleError({
				errorCode: 'VersionCheckerError',
				error,
				friendlyMessage: `Please check your internet connection and try again.`,
			});
			return null;
		}

		return (
			<div className={styles['root']}>
				<Loader text="version" />
			</div>
		);
	}

	const message =
		healthRes.message ||
		`A new version of the Asaph App is available in the ${
			isIOS ? 'App' : 'Google Play'
		} Store. Please update your app to version ${
			healthRes.appVersion
		} to continue using Asaph with our latest features.`;

	if (
		!isCompatibleVersion({
			liveVersion: healthRes.apiVersion,
			requiredVersion: version,
		})
	) {
		return (
			<PublicLayout
				logo={<Logo mini />}
				renderFooter={
					<AsaphButton
						onClick={() => {
							window.open(isIOS ? appStoreUrl : googlePlayUrl);
						}}
						label="Update Now"
						color="blue"
						fullWidth
					/>
				}
			>
				<div className={styles['title']}>Update Asaph for Mobile</div>
				<div className={styles['subtitle']}>{message}</div>
			</PublicLayout>
		);
	}

	return <>{children}</>;
};

const isCompatibleVersion = ({
	liveVersion,
	requiredVersion,
}: {
	liveVersion: string;
	requiredVersion: string;
}): boolean => {
	try {
		const liveParts = liveVersion.split('.').map(Number);
		const requiredParts = requiredVersion.split('.').map(Number);
		for (let i = 0; i < 2; i++) {
			const livePart = liveParts[i] || 0;
			const requiredPart = requiredParts[i] || 0;
			if (livePart !== requiredPart) {
				return false;
			}
		}
		return true;
	} catch (err) {
		console.error(err);
		return false;
	}
};
